import { createContext, createElement, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { STORED_EVENT, carries, normaliseText, readDraft, removeDraft, writeDraft } from "./draft-store";

export { DRAFT_PREFIX, STORED_EVENT, carries, textsOf } from "./draft-store";

// When the person last pressed a key or a pointer anywhere on the page: a change of a field's value right after it is theirs, any other
// change is the screen's own (an automatic fill, a refetch) and never replaces or deletes the text kept on the device (S 2.7).
let lastGesture = 0;
let listening = false;
function trackGestures() {
  if (listening || typeof document === "undefined") return;
  listening = true;
  const mark = () => { lastGesture = Date.now(); };
  document.addEventListener("pointerdown", mark, true);
  document.addEventListener("keydown", mark, true);
}
const GESTURE_MS = 1500;
const MAX_REAPPLY = 3;

const storage = (): Storage | null => {
  try { return typeof window === "undefined" ? null : window.localStorage; } catch { return null; }
};

const DraftOwnerContext = createContext<string | null>(null);

export interface DraftOptions {
  enabled?: boolean;
  /**
   * Set to true by the field on each change the person makes in it (typing, pasting, formatting), so only their own text is kept – never a
   * value the screen put in the field (another record's text, its data arriving). Without it, a change right after a key or pointer press counts.
   */
  edited?: { current: boolean };
}

/** Names whose device-kept text the fields below belong to (the signed-in user's id); without one no field keeps a draft. */
export function DraftOwner({ owner, children }: { owner: string | null | undefined; children: ReactNode }) {
  return createElement(DraftOwnerContext.Provider, { value: owner ?? null }, children);
}

/** The full key of a field's draft: the signed-in user, then the field's own name; null while nobody is signed in or the field keeps none. */
export function useDraftKey(name: string | null | undefined): string | null {
  const owner = useContext(DraftOwnerContext);
  return owner && name ? `${owner}:${name}` : null;
}

/**
 * Keeps unsaved text on this device, per user and field, so a lapsed session, a reload or a closed tab does not lose it (S 2.7).
 *
 *   const draft = useDraft(`${userId}:${caseId}:rationale`, text, setText);
 *   …after a successful save: draft.clear();   (a save whose request carried the text lets it go by itself)
 *
 * On mount, a stored draft that differs from the current value is restored through `setValue` and `restored` turns true so the screen
 * can say so; one equal to the current value was saved and is dropped. Until the person acts on the page, the restored text also wins over
 * a value the screen fills in by itself afterwards (its data arriving, a default applied): the saved and kept work comes first, an automatic
 * fill only reaches an empty field. Only changes the person makes are kept; once the API stored a request carrying the text, it is let go.
 * Pass `null` as the key while the owner is unknown. Text fields take a `draft` name instead (`Textarea`, `RichTextEditor`).
 */
export function useDraft(key: string | null, value: string, setValue: (value: string) => void, { enabled = true, edited }: DraftOptions = {}) {
  const [restored, setRestored] = useState(false);
  const loadedFor = useRef<string | null>(null);
  const baseline = useRef(value);
  const original = useRef(value);
  const current = useRef(value);
  current.current = value;
  const setRef = useRef(setValue);
  setRef.current = setValue;
  // The restored text and when it came back, while it still holds against the screen's own fills.
  const holding = useRef<{ text: string; at: number; reapplied: number } | null>(null);

  useEffect(() => { trackGestures(); }, []);

  useEffect(() => {
    if (!key || !enabled || loadedFor.current === key) return;
    loadedFor.current = key;
    baseline.current = value;
    original.current = value;
    holding.current = null;
    const store = storage();
    const stored = store ? readDraft(store, key) : null;
    if (!stored || !stored.value.trim()) return;
    if (normaliseText(stored.value) === normaliseText(value)) { if (store) removeDraft(store, key); return; }
    holding.current = { text: stored.value, at: Date.now(), reapplied: 0 };
    setValue(stored.value);
    setRestored(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  useEffect(() => {
    if (!key || !enabled || loadedFor.current !== key) return;
    const hold = holding.current;
    if (hold) {
      if (lastGesture > hold.at) holding.current = null;
      else if (value !== hold.text) {
        // The screen replaced the restored text by itself (its data arrived after the restore): the officer's text comes back, and what
        // the screen brought is what "Discard" returns to.
        original.current = value;
        baseline.current = value;
        if (hold.reapplied < MAX_REAPPLY) { hold.reapplied++; setRef.current(hold.text); }
        else holding.current = null;
        return;
      }
    }
    // A field that knows its own input says so (`edited`); otherwise a change right after a key or a pointer press counts as the person's.
    const theirs = edited ? edited.current : Date.now() - lastGesture < GESTURE_MS;
    if (edited) edited.current = false;
    if (!theirs) return;
    const timer = window.setTimeout(() => {
      const store = storage();
      if (!store) return;
      if (!value.trim() || value === baseline.current) removeDraft(store, key);
      else writeDraft(store, key, value);
    }, 400);
    return () => window.clearTimeout(timer);
  }, [key, enabled, value]);

  // The API stored a request carrying this text: the server has it, the device copy is let go.
  useEffect(() => {
    if (!key || !enabled || typeof window === "undefined") return;
    const onStored = (e: Event) => {
      const text = current.current;
      if (!text.trim() || !carries((e as CustomEvent<{ body?: unknown }>).detail?.body, text)) return;
      const store = storage();
      if (store) removeDraft(store, key);
      baseline.current = text;
      holding.current = null;
      setRestored(false);
    };
    window.addEventListener(STORED_EVENT, onStored);
    return () => window.removeEventListener(STORED_EVENT, onStored);
  }, [key, enabled]);

  const clear = useCallback(() => {
    const store = storage();
    if (key && store) removeDraft(store, key);
    baseline.current = current.current;
    holding.current = null;
    setRestored(false);
  }, [key]);

  const discard = useCallback(() => {
    const store = storage();
    if (key && store) removeDraft(store, key);
    baseline.current = original.current;
    holding.current = null;
    setRestored(false);
    setRef.current(original.current);
  }, [key]);

  return { restored, clear, discard };
}

/** `useDraft` for a text field named `name` within the signed-in user's drafts (see `useDraftKey`). */
export function useFieldDraft(name: string | null | undefined, value: string, setValue: (value: string) => void, options?: DraftOptions) {
  return useDraft(useDraftKey(name), value, setValue, options);
}
